
import { mapActions, mapState } from "pinia"
import { useStore } from "../../store/PiniaStore"
import { defineComponent } from "vue"
import PageHeader from "../PageHeader.vue"
import LoadingIndicator from "../LoadingIndicator.vue"
import { IAttachment, IOrderQuickFormatRowSnapshot, IOrderRow } from "../../lib/InterfacesAndTypes"
import { SPECIAL_FLAG_ORDER, SPECIAL_FLAG_COLORS, SpecialFlag, parseSpecialFlags, rollupSpecialFlags } from "../../lib/HelperFns"
import { VueRouterInst } from "../../InitRoutesPlugins"
import { saveAs } from "file-saver"
import { getAttachmentDisplayKind } from "@/util/attachmentDisplay"

const SAVING_DELAY_MS = 120

export default defineComponent({
	name: 'OrderDetailScreen',
	async mounted() {
		await this.loadData()
		this.collapseAllRows()
	},
	data: () => ({
		loading: {
			page: false,
		},
		isSaving: false,
		orderAttachmentIsUploading: false,
		isInitial: true,
		rowsOpened: [] as number[],
		quickFormatCheckedRows: [] as IOrderRow[],
		specialFlagOrder: SPECIAL_FLAG_ORDER,
		flagsFilterActive: false,
	}),
	components: {
		PageHeader,
		LoadingIndicator,
	},
	computed: {
		...mapState(useStore, ['orders', 'currentlySelectedOrder', 'orderUILayout', 'FABSelectedQuickFormatRows']),
		order: (state) => {
			return state.currentlySelectedOrder
		},
		displayedOrderRows: (state) => {
			const rows = state.currentlySelectedOrder?.orderRows ?? []
			if (!state.flagsFilterActive) {
				return rows
			}
			return rows.filter((row) => parseSpecialFlags(row.specialFlags).length > 0)
		},
		rowsCompleted: (state) => {
			return state.currentlySelectedOrder?.orderRows?.filter((row) => row.isClosed).length ?? 0
		},
		rowsTotal: (state) => {
			return state.currentlySelectedOrder?.orderRows?.length ?? 0
		},
		orderCreatedAt: (state) => {
			if (!state.currentlySelectedOrder) {
				return null
			}
			return state.currentlySelectedOrder.createdAtPretty
		},
	},
	methods: {
		...mapActions(useStore, ['selectFirstOrder', 'selectOrderById', 'fetchOrderById',
			'setOrderUILayout', 'updateOrderRowCourtsSent', 'updateOrderRowCourtsReceived', 'updateOrderRowHitStatus',
			'updateOrderRowInternalHitStatus', 'updateOrderRowNotes', 'updateOrderRowIsClosed', 'updateOrderRowSpecialFlags', 'setOrderIsClosed', 'deleteOrder',
			'postOrderRowUploadAttachment', 'postConfirmAttachment', 'deleteOrderRowAttachment', 'putPresignedAttachmentUpload',
			'uploadOrderAttachmentFile', 'deleteOrderAttachmentAndRefresh',
			'addQuickFormatOrderRow', 'removeQuickFormatOrderRow', 'syncQuickFormatOrderRowsFromCheckedList',
		]),

		async loadData(useSpinner: boolean = true) {
			if (useSpinner) {
				this.loading.page = true;
			}
			const orderId = Number.parseInt(this.$route.params.orderId)
			console.log('params', this.$route.params)
			await this.fetchOrderById(orderId)
			this.selectOrderById(orderId)
			if (useSpinner) {
				this.loading.page = false;
			}
		},

		findAndCheckQuickFormatRows() {
			if (!this.order?.orderRows?.length) {
				if (this.quickFormatCheckedRows.length > 0) {
					this.quickFormatCheckedRows = []
				}
				return
			}
			const orderId = this.order.orderId
			const next: IOrderRow[] = []
			const snaps = this.FABSelectedQuickFormatRows as IOrderQuickFormatRowSnapshot[]
			for (const snap of snaps) {
				if (snap.orderId !== orderId) {
					continue
				}
				const live = this.order.orderRows.find((r) => r.rowId === snap.rowId)
				if (live) {
					next.push(live)
				}
			}
			const sameLength = next.length === this.quickFormatCheckedRows.length
				&& next.every((r, i) => r === this.quickFormatCheckedRows[i])
			if (sameLength) {
				return
			}
			this.quickFormatCheckedRows = next
		},

		onQuickFormatRowChecked(checkedList: IOrderRow[], row?: IOrderRow) {
			if (!this.order) {
				return
			}
			if (row == null) {
				const oid = this.order.orderId
				const forOrder = checkedList.filter((r) => r.orderId === oid)
				this.syncQuickFormatOrderRowsFromCheckedList(oid, forOrder)
				return
			}
			if (checkedList.includes(row)) {
				this.addQuickFormatOrderRow(row)
			} else {
				this.removeQuickFormatOrderRow(row)
			}
		},

		async onRowAttachmentFileSelected(row: IOrderRow, event: Event) {
			const input = event.target as HTMLInputElement
			const files = input.files
			if (!files?.length) {
				return
			}
			const file = files[0]
			row.attachmentIsUploading = true
			try {
				const reg = await this.postOrderRowUploadAttachment({
					orderId: row.orderId,
					rowId: row.rowId,
					filename: file.name,
					contentType: file.type || "application/octet-stream",
					sizeBytes: file.size,
				})
				await this.putPresignedAttachmentUpload(reg.uploadUrl, file, reg.headers)
				await this.postConfirmAttachment({ attachmentId: reg.attachmentId })
				await this.fetchOrderById(row.orderId)
			} catch (error) {
				console.error(error)
				this.showOrderDetailErrorToast("Failed to upload attachment.")
			} finally {
				row.attachmentIsUploading = false
				input.value = ""
			}
		},

		async onOrderAttachmentFileSelected(event: Event) {
			if (!this.order) {
				return
			}
			if (this.order.isClosed) {
				this.showOrderDetailErrorToast("Order is closed; attachments are disabled.")
				return
			}
			const input = event.target as HTMLInputElement
			const files = input.files
			if (!files?.length) {
				return
			}
			const file = files[0]
			this.orderAttachmentIsUploading = true
			try {
				await this.uploadOrderAttachmentFile(this.order.orderId, file)
			} catch (error) {
				console.error(error)
				this.showOrderDetailErrorToast("Failed to upload attachment.")
			} finally {
				this.orderAttachmentIsUploading = false
				input.value = ""
			}
		},

		async downloadAttachment(attachment: IAttachment) {
			if (!attachment.url) {
				this.showOrderDetailErrorToast("No download URL for this attachment.")
				return
			}
			try {
				const res = await fetch(attachment.url)
				if (!res.ok) {
					throw new Error(String(res.status))
				}
				const blob = await res.blob()
				saveAs(blob, attachment.filename)
			} catch (error) {
				console.error(error)
				this.showOrderDetailErrorToast("Failed to download attachment.")
			}
		},

		async deleteOrderAttachment(attachment: IAttachment) {
			if (!this.order) {
				return
			}
			try {
				await this.deleteOrderAttachmentAndRefresh(this.order.orderId, attachment.attachmentId)
			} catch (error) {
				console.error(error)
				this.showOrderDetailErrorToast("Failed to delete attachment.")
			}
		},

		async deleteAttachment(row: IOrderRow, attachment: IAttachment) {
			try {
				await this.deleteOrderRowAttachment({
					orderId: row.orderId,
					rowId: row.rowId,
					attachmentId: attachment.attachmentId,
				})
				await this.fetchOrderById(row.orderId)
			} catch (error) {
				console.error(error)
				this.showOrderDetailErrorToast("Failed to delete attachment.")
			}
		},

		async toggleCourtsSent(row: IOrderRow) {
			row.isSaving = true
			setTimeout(() => {
				row.isSaving = false
			}, SAVING_DELAY_MS)
			row.courtsSent = !row.courtsSent
			console.log(`toggleCourtsSent ${row.orderId} row ${row.rowId}, setting isSaving to true`, row)
			try {
				const result = await this.updateOrderRowCourtsSent(row.orderId, row.rowId, row.courtsSent)
				console.log('toggleCourtsSent result', result)
			} catch (error) {
				console.error('toggleCourtsSent error', error)
				this.showOrderDetailErrorToast(`Failed to update courts sent status on row ${row.rowId}.`)
			}
		},
		async toggleCourtsReceived(row: IOrderRow) {
			row.isSaving = true
			setTimeout(() => {
				row.isSaving = false
			}, SAVING_DELAY_MS)
			row.courtsReceived = !row.courtsReceived
			console.log(`toggleCourtsReceived ${row.orderId} row ${row.rowId}, setting isSaving to true`, row)
			try {
				const result = await this.updateOrderRowCourtsReceived(row.orderId, row.rowId, row.courtsReceived)
				console.log('toggleCourtsReceived result', result)
			} catch (error) {
				console.error('toggleCourtsReceived error', error)
				this.showOrderDetailErrorToast(`Failed to update courts received status on row ${row.rowId}.`)
			}
		},
		async updateNotes(row: IOrderRow) {
			row.isSaving = true
			console.log(`updateNotes ${row.orderId} row ${row.rowId}, setting isSaving to true`, row)
			row.notes = row.notes.trim()
			try {
				const result = await this.updateOrderRowNotes(row.orderId, row.rowId, row.notes)
				console.log('updateNotes result', result)
			} catch (error) {
				console.error('updateNotes error', error)
				this.showOrderDetailErrorToast(`Failed to update notes on row ${row.rowId}.`)
			}
		},
		setHitStatusFromToggle(row: IOrderRow, value: 'Hit' | 'NR') {
			if (row.hitStatus === value) {
				this.updateHitStatus(row, '')
			} else {
				this.updateHitStatus(row, value)
			}
		},
		async updateHitStatus(row: IOrderRow, hitStatus: string) {
			row.isSaving = true
			setTimeout(() => {
				row.isSaving = false
			}, SAVING_DELAY_MS)
			row.hitStatus = hitStatus
			console.log(`updateHitStatus ${row.orderId} row ${row.rowId}, setting isSaving to true`, row)
			try {
				const result = await this.updateOrderRowHitStatus(row.orderId, row.rowId, row.hitStatus)
				console.log('updateHitStatus result', result)
			} catch (error) {
				console.error('updateHitStatus error', error)
				this.showOrderDetailErrorToast(`Failed to update hit status on row ${row.rowId}.`)
			}
		},
		setInternalHitStatusFromToggle(row: IOrderRow, value: 'Hit') {
			const current = row.internalHitStatus ?? ''
			if (current === value) {
				this.updateInternalHitStatus(row, '')
			} else {
				this.updateInternalHitStatus(row, value)
			}
		},
		async updateInternalHitStatus(row: IOrderRow, internalHitStatus: string) {
			row.isSaving = true
			setTimeout(() => {
				row.isSaving = false
				console.log(`updateInternalHitStatus ${row.orderId} row ${row.rowId}, setting isSaving to false`, row)
			}, SAVING_DELAY_MS)
			row.internalHitStatus = internalHitStatus
			console.log(`updateInternalHitStatus ${row.orderId} row ${row.rowId}, setting isSaving to true`, row)
			try {
				const result = await this.updateOrderRowInternalHitStatus(row.orderId, row.rowId, row.internalHitStatus ?? '')
				console.log('updateInternalHitStatus result', result)
			} catch (error) {
				console.error('updateInternalHitStatus error', error)
				this.showOrderDetailErrorToast(`Failed to update WM hit status on row ${row.rowId}.`)
			}
		},
		async updateRowIsClosed(row: IOrderRow, isClosed: boolean) {
			row.isSaving = true
			setTimeout(() => {
				row.isSaving = false
			}, SAVING_DELAY_MS)
			console.log(`markRowAsCompleted ${row.orderId} row ${row.rowId}, setting isSaving to ${isClosed}`, row)
			row.isClosed = isClosed
			try {
				const result = await this.updateOrderRowIsClosed(row.orderId, row.rowId, row.isClosed)
				console.log('markRowAsCompleted result', result)
			} catch (error) {
				console.error('markRowAsCompleted error', error)
				this.showOrderDetailErrorToast(`Failed to mark row as completed on row ${row.rowId}.`)
			}
		},

		flagColor(flag: SpecialFlag) {
			return SPECIAL_FLAG_COLORS[flag]
		},
		flagsOf(row: IOrderRow): SpecialFlag[] {
			return parseSpecialFlags(row.specialFlags)
		},
		orderRollupFlags(): SpecialFlag[] {
			return this.order ? rollupSpecialFlags(this.order) : []
		},
		async onSpecialFlagsChange(row: IOrderRow, flags: SpecialFlag[]) {
			row.isSaving = true
			setTimeout(() => {
				row.isSaving = false
			}, SAVING_DELAY_MS)
			row.specialFlags = SPECIAL_FLAG_ORDER.filter((flag) => flags.includes(flag)).join(',')
			try {
				await this.updateOrderRowSpecialFlags(row.orderId, row.rowId, row.specialFlags)
			} catch (error) {
				console.error('onSpecialFlagsChange error', error)
				this.showOrderDetailErrorToast(`Failed to update special flags on row ${row.rowId}.`)
			}
		},

		async toggleOrderIsClosed(isClosed: boolean) {
			try {
				this.isSaving = true
				console.log(`toggleOrderIsClosed ${this.order.orderId}, setting isSaving to true`, this.order)
				await this.setOrderIsClosed(this.order.orderId, isClosed)
				this.order.isClosed = isClosed
			} catch (error) {
				console.error('toggleOrderIsClosed error', error)
				this.showOrderDetailErrorToast(`Failed to toggle order is closed status on order ${this.order.orderId}.`)
			} finally {
				this.isSaving = false
				console.log(`toggleOrderIsClosed ${this.order.orderId}, setting isSaving to false`, this.order)
			}
		},

		async tryDeleteOrder() {
			try {
				this.isSaving = true
				console.log(`tryDeleteOrder ${this.order.orderId}, setting isSaving to true`, this.order)
				const result = await this.deleteOrder(this.order.orderId)
				this.$buefy.toast.open({
					message: 'Order deleted successfully.',
					type: 'is-success',
					duration: 3000,
				})
				VueRouterInst.push('/orders')
			} catch (error) {
				console.error('tryDeleteOrder error', error)
				this.showOrderDetailErrorToast(`Failed to delete order ${this.order.orderId}.`)
			} finally {
				this.isSaving = false
				console.log(`tryDeleteOrder ${this.order.orderId}, setting isSaving to false`, this.order)
			}
		},

		showOrderDetailErrorToast(message: string) {
			this.$buefy.toast.open({
				message: message + `<br>Please <a href="javascript:window.location.reload()">reload the page</a> and try again.`,
				type: 'is-danger',
				indefinite: true,
			})
		},
		async copyToClipboard(value: string) {
			await navigator.clipboard.writeText(value)
			this.$buefy.toast.open({
				message: `Copied '${value}' to clipboard!`,
				type: 'is-success',
				duration: 3000,
			})
		},
		getRowClass(row: IOrderRow) {
			if (row.isClosed) {
				if (row.hitStatus === 'Hit' || row.internalHitStatus === 'Hit') {
					return 'order-hit-bg'
				} else if (row.hitStatus === 'NR') {
					return 'order-nr-bg'
				}
			}
			return `null ${row.hitStatus} ${row.internalHitStatus} end`
		},
		getRowCellClass(row: IOrderRow) {
			if (row.hitStatus === 'Hit' || row.internalHitStatus === 'Hit') {
				return 'order-hit-bg'
			} else if (row.hitStatus === 'NR') {
				return 'order-nr-bg'
			}
			return `null ${row.hitStatus} ${row.internalHitStatus} end`
		},
		getAttachmentClass(attachment: IAttachment) {
			const kind = getAttachmentDisplayKind(attachment)
			if (kind === "pdf") {
				return "attachment-pdf"
			}
			if (kind === "doc") {
				return "attachment-doc"
			}
			if (kind === "ppt") {
				return "attachment-ppt"
			}
			if (kind === "xls") {
				return "attachment-xls"
			}
			if (kind === "image") {
				return "attachment-other"
			}
			if (kind === "csv" || kind === "text") {
				return "attachment-other"
			}
			return "attachment-other"
		},
		getAttachmentIcon(attachment: IAttachment) {
			const kind = getAttachmentDisplayKind(attachment)
			if (kind === "pdf") {
				return "file-document-outline"
			}
			if (kind === "doc") {
				return "file-word-outline"
			}
			if (kind === "ppt") {
				return "file-powerpoint-outline"
			}
			if (kind === "xls") {
				return "file-excel-outline"
			}
			if (kind === "csv") {
				return "file-delimited-outline"
			}
			if (kind === "image") {
				return "file-image-outline"
			}
			if (kind === "text") {
				return "file-document-outline"
			}
			return "file-outline"
		},
		truncateAttachmentFilename(filename: string) {
			const maxAttachmentFilenameDisplayChars = 24
			if (filename.length <= maxAttachmentFilenameDisplayChars) {
				return filename
			}
			return `${filename.slice(0, maxAttachmentFilenameDisplayChars)}…`
		},

		selectDetailLayout() {
			this.setOrderUILayout('detail')
			this.expandAllRows()
		},
		selectExpandableDetailLayout() {
			this.setOrderUILayout('expandable-detail')
			this.collapseAllRows()
		},

		collapseAllRows() {
			this.rowsOpened = []
		},
		expandAllRows() {
			this.rowsOpened = this.currentlySelectedOrder?.orderRows.map((row) => row.rowId) ?? []
		},

		checkIfOrderCanClose() {
			return this.currentlySelectedOrder?.orderRows.every(row => row.isClosed)
		},
	},
	//TODO: keep an eye on perf here
	watch: {
		order: {
			handler() {
				this.findAndCheckQuickFormatRows()
			},
			deep: true,
			immediate: true,
		},
		orderUILayout() {
			this.$nextTick(() => {
				this.findAndCheckQuickFormatRows()
			})
		},
		FABSelectedQuickFormatRows: {
			handler() {
				this.findAndCheckQuickFormatRows()
			},
			deep: true,
		},
	},
})
